import { t } from '../../i18n';
import type { KeyBindingsManager } from '../../settings/KeyBindings';
import type { SpaceTimeClient } from '../../spacetime/SpaceTimeClient';
import { applyRuntimeEnvironment, type SpaceTimeConfig } from '../../spacetime/config';
import type { LoginPromptHandle } from '../../ui/LoginPrompt';

type AuthConfig = SpaceTimeConfig;

interface RunAuthFlowParams {
  loginPrompt: LoginPromptHandle;
  spaceTimeClient: SpaceTimeClient;
  keyBindings: KeyBindingsManager;
  config: AuthConfig;
}

export async function runAuthFlow(params: RunAuthFlowParams): Promise<void> {
  const { loginPrompt, spaceTimeClient, keyBindings, config } = params;
  let authenticated = false;
  let connectedEnvironmentId: 'local' | 'maincloud' | null = null;

  while (!authenticated) {
    try {
      const authAction = await loginPrompt.waitForAction();
      const selectedEnvironmentId = authAction.environmentId;

      if (connectedEnvironmentId !== selectedEnvironmentId) {
        if (spaceTimeClient.isConnected()) {
          await spaceTimeClient.disconnect({ skipLogout: true });
        }
        applyRuntimeEnvironment(config, selectedEnvironmentId);
        await spaceTimeClient.connect();
        connectedEnvironmentId = selectedEnvironmentId;
      } else if (!spaceTimeClient.isConnected()) {
        await spaceTimeClient.connect();
      }

      if (authAction.action === 'cancel') {
        const username = 'admin';
        const password = 'admin-password';
        await spaceTimeClient.auth.login({ username, password });
        const myPlayer = await spaceTimeClient.players.waitForMyPlayerSnapshot();
        if (!myPlayer) {
          throw new Error('Login succeeded but my_player was not available');
        }
        config.username = username;
        config.password = password;
        config.playerId = myPlayer.playerId;
        keyBindings.setStorageScope(myPlayer.playerId);
        authenticated = true;
      } else {
        const { action, username, password } = authAction;
        if (action === 'register') {
          await spaceTimeClient.auth.registerPlayer({
            playerId: username,
            username,
            password,
            displayName: username
          });
        }
        await spaceTimeClient.auth.login({ username, password });
        const myPlayer = await spaceTimeClient.players.waitForMyPlayerSnapshot();
        if (!myPlayer) {
          throw new Error('Login succeeded but my_player was not available');
        }
        config.username = username;
        config.password = password;
        config.playerId = myPlayer.playerId;
        keyBindings.setStorageScope(myPlayer.playerId);
        authenticated = true;
      }
      loginPrompt.close();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('app.authFailed');
      loginPrompt.showError(message);
    }
  }
}
